// PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue 3 merges the old `$listeners` object
// into `$attrs` as `onXxx` keys -- there is no separate listeners bag any more. Most
// `v-on="$listeners"` call sites can just become `v-bind="$attrs"` (attrs now carries
// the handlers too). But a subset of components explicitly set `inheritAttrs: false`
// and forwarded ONLY `$listeners` down to a child/native element -- deliberately
// excluding raw (non-listener) attrs, which were otherwise dropped. Blanket-binding
// `$attrs` at those call sites would newly leak raw attrs that were never forwarded
// before. These two helpers let call sites keep that exact "listeners only" contract.

type EventHandler = (...args: unknown[]) => void;

// Vue 3 merges multiple bindings for the same event on one element into an array
// (e.g. a `v-model` listener plus an explicit `@input` on the same tag both land in
// `onInput`, as `onInput: [fn1, fn2]`) -- both helpers below must treat an array of
// functions as a valid listener value, not just a bare function, or they silently
// drop one of the two handlers.
function isListenerValue(value: unknown): value is EventHandler | EventHandler[] {
    return typeof value === 'function' || (Array.isArray(value) && value.every((item) => typeof item === 'function'));
}

/**
 * Picks the listener-shaped (`onXxx`) keys back out of `$attrs`, for use with
 * `v-bind="pickListenerAttrs($attrs)"` when forwarding "listeners only" (no raw attrs)
 * to a child component -- the Vue-3-native replacement for `v-on="$listeners"` in
 * `inheritAttrs: false` components that never spread `$attrs` elsewhere.
 */
export function pickListenerAttrs(attrs: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(attrs).filter(([ key, value ]) => /^on[A-Z]/.test(key) && isListenerValue(value)),
    );
}

/**
 * The complement of `pickListenerAttrs`: everything in `$attrs` except the handlers.
 *
 * For a component that sets `inheritAttrs: false` and forwards its listeners to a child
 * while still wanting the ordinary attributes (`data-test-id` and friends) on its own
 * root. Leaving `inheritAttrs` alone is not an option there: Vue 3 would put the handlers
 * on the root element as native listeners as well as on the child, so a bubbling native
 * event fires the caller's handler a second time with the raw Event.
 */
export function omitListenerAttrs(attrs: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(attrs).filter(([ key, value ]) => !(/^on[A-Z]/.test(key) && isListenerValue(value))),
    );
}

/**
 * Rebuilds a bare-event-name listeners object (e.g. `{ click: fn }`) from `$attrs`'
 * `onXxx` keys, for `v-on="..."` bindings on native elements that still expect the
 * pre-Vue-3 `$listeners` shape (bare names, not `onXxx`). Array-valued entries (see
 * `isListenerValue` above) are flattened into a single handler that calls each in turn.
 */
export function toEventListeners(attrs: Record<string, unknown>): Record<string, EventHandler> {
    return Object.entries(attrs).reduce((acc, [ key, value ]) => {
        if (/^on[A-Z]/.test(key) && isListenerValue(value)) {
            const eventName = key[2].toLowerCase() + key.slice(3);
            acc[eventName] = Array.isArray(value)
                ? (...args: unknown[]) => value.forEach((fn) => fn(...args))
                : value;
        }
        return acc;
    }, {} as Record<string, EventHandler>);
}
